import * as React from 'react'
import { styled } from 'next-yak'

import * as S from '../../styles'
import { useTransition } from '../../behaviours'
import { Spinner } from '../Spinner'
import type { ButtonProps } from '../../styles/button'
import type { PolymorphicComponent, YakComponentOf } from '@owl-nest/yak/extra-runtime'

type ExtraButtonProps = {
  active?: boolean
  /** Palette shared with Button (default 'blue'); a runtime theme still overrides it. */
  color?: ButtonProps['color']
  disabled?: boolean
  fitToParent?: boolean
  inverted?: boolean
  /** 'secondary' renders the stroke/outline variant, like Button. */
  kind?: ButtonProps['kind']
  loading?: boolean
  prefixIcon?: React.ReactNode
  /** 'big' bumps padding/height/font for prominent CTAs; defaults to 'small' (existing behaviour). */
  size?: 'small' | 'big'
  suffixIcon?: React.ReactNode
}

export type RoundButtonProps = ExtraButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>

function RoundButtonComponent({
  active = false,
  children,
  color,
  disabled = false,
  fitToParent = false,
  inverted = false,
  kind,
  loading = false,
  prefixIcon,
  size = 'small',
  suffixIcon,
  ...buttonProps
}: RoundButtonProps): React.ReactElement<any> {
  const { step, unmounted, onTransitionEnd } = useTransition(loading)

  return (
    <S.button.RoundButton
      $active={active}
      $color={color}
      disabled={disabled}
      $fitToParent={fitToParent}
      $inverted={inverted}
      $kind={kind}
      $loading={loading}
      $size={size}
      $step={step}
      $unmounted={unmounted}
      {...buttonProps}
    >
      {prefixIcon && prefixIcon}
      <S.button.Label>{children}</S.button.Label>
      <Spinner onTransitionEnd={onTransitionEnd} small />
      {suffixIcon && suffixIcon}
    </S.button.RoundButton>
  )
}

export const RoundButton = styled(RoundButtonComponent)`` as YakComponentOf<
  PolymorphicComponent<'button', ExtraButtonProps>
>
